import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IPlayerStat} from "data/stores/player_stats/player_stats.store";
import type {IContestStat} from "data/stores/contest_player_stats/contest_player_stats.store";
import {PlayerPosition, PlayerCategory, PlayerGamePosition} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {PlayerUtils} from "data/utils/player_utils.utils";
import {DateTime} from "luxon";
import {TournamentStatus} from "data/enums/torunaments";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import {map, get, sumBy} from "lodash";
import {IGamePlayer, type IPlayerCalculations} from "data/utils/player.calculations";

interface IStat {
	name: string;
	value: number;
}
interface IParams {
	playerID: number;
	contestID: number;
}

export interface IPlayerStatsController extends ViewController<IParams> {
	i18n: ILocalizationStore;
	get player(): IGamePlayer | undefined;
	get playerCategory(): PlayerCategory;
	get contestStats(): IContestStat | undefined;
	get playerStats(): IPlayerStat | undefined;
	get playerStatsString(): string;
	get tournamentDate(): string;
	get versus(): string;
	get opponentSquadName(): string;

	get statsPlayerByContest(): IStat[];
	get totalPoints(): number;
	get tournamentStatus(): TournamentStatus;
	get opponentSquad(): string;
}

@injectable()
export class PlayerStatsController implements IPlayerStatsController {
	@observable private _playerID = 0;
	@observable private _contestID: number | undefined = 0;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.TournamentsStore) private readonly _tournamentsStore: ITournamentsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IParams) {
		this._playerID = param.playerID;
		this._contestID = param.contestID;
	}

	onChange(param: IParams) {
		this.init(param);
	}

	private buildAvgStatValue(stat: number, fraction: number = 3) {
		return String(stat.toFixed(fraction)).replace(/^0\./, ".");
	}

	get contestStats() {
		return this.player?.contestStats;
	}

	get playerStats() {
		return this.player?.playerStats;
	}

	get playerStatsString() {
		const isPitcher = this.player?.position === PlayerPosition.Pitcher;
		const {ERA, W, SO, BB, AVG, RBI, HR, R} = this.translations;

		const pitcherStats = this.playerStats?.pitching;
		if (isPitcher && pitcherStats) {
			return `${pitcherStats.era.toFixed(2)}${ERA} | ${pitcherStats.wins}${W} | ${
				pitcherStats.strikeOuts
			}${SO} | ${pitcherStats.walks}${BB}`;
		}

		const hittingStats = this.playerStats?.hitting;

		if (!isPitcher && hittingStats) {
			return `${this.buildAvgStatValue(hittingStats.avg)}${AVG} | ${
				hittingStats.rbi
			}${RBI} | ${hittingStats.homeRuns}${HR} | ${hittingStats.runs}${R}`;
		}

		return "--";
	}

	get player() {
		return this._playerCalculations.getGamePlayerById(this._playerID, this._contestID);
	}

	get playerCategory(): PlayerCategory {
		return this.player?.contestStats?.category || PlayerCategory.NORMAL;
	}

	private get translations() {
		return {
			ERA: this.i18n.t("player.stats.era", "ERA"),
			W: this.i18n.t("player.stats.w", "W"),
			SO: this.i18n.t("player.stats.so", "SO"),
			BB: this.i18n.t("player.stats.bb", "BB"),
			AVG: this.i18n.t("player.stats.avg", "AVG"),
			RBI: this.i18n.t("player.stats.rbi", "RBI"),
			HR: this.i18n.t("player.stats.hr", "HR"),
			R: this.i18n.t("player.stats.r", "R"),
		};
	}

	get opponentSquadName(): string {
		return this.player?.opponent?.abbreviation || "";
	}

	get statsPlayerByContest(): IStat[] {
		return this.fullStatsPlayerByContest.filter((it) => it.value);
	}

	get totalPoints(): number {
		const multiplier = this.player?.contestStats?.pointsMultiplier || 1;
		return sumBy(this.statsPlayerByContest, "value") * multiplier;
	}

	get fullStatsPlayerByContest(): IStat[] {
		const player = this.player;
		if (!player) {
			return [];
		}

		if (player?.gamePosition === PlayerGamePosition.Pitcher) {
			const pitcherPoints = PlayerUtils.statsPoints.pitching;
			const pitchingStats = this.pitchingStats;

			return map(pitcherPoints, (multiplyPoints, key) => {
				const multiply = get(pitchingStats, key, 0);
				return {
					name: this.i18n.t(`scoring.pitcher_x.${key}`, key, {X: multiply}),
					value: multiplyPoints * multiply,
				};
			});
		}
		const hittingPoints = PlayerUtils.statsPoints.hitting;
		const hittingStats = this.hittingStats;

		return map(hittingPoints, (multiplyPoints, key) => {
			const multiply = get(hittingStats, key, 0);
			return {
				name: this.i18n.t(`scoring.hitters_x.${key}`, key, {X: multiply}),
				value: multiplyPoints * multiply,
			};
		});
	}

	get pitchingStats() {
		const stats = this.contestStats;
		return stats?.pitching || {};
	}

	get hittingStats() {
		const stats = this.contestStats;
		return stats?.hitting || {};
	}

	get tournamentDate(): string {
		if (!this.tournament?.startDate) {
			return "";
		}
		const dateTime = DateTime.fromISO(this.tournament?.startDate);
		if (!dateTime.isValid) {
			return "";
		}
		return dateTime.toFormat("h:mm a ZZZZ");
	}

	get versus(): string {
		const squadId = this.player?.squadId;
		const homeVersus = this.i18n.t("game.versus.home", "VS");
		const awayVersus = this.i18n.t("game.versus.away", "@");
		return squadId === this.tournament?.homeSquadId ? homeVersus : awayVersus;
	}

	get tournamentStatus() {
		return this.tournament?.status || TournamentStatus.SCHEDULED;
	}

	get opponentSquad(): string {
		const abbr = this.player?.opponent?.abbreviation;
		return abbr ? `${this.versus} ${abbr}` : "";
	}

	private get tournament() {
		if (!this.contestStats?.tournamentId) {
			return;
		}
		return this._tournamentsStore.getTournamentById(this.contestStats?.tournamentId);
	}
}
